/**
 * Activity Logs — PII Access Audit Trail (admin-only).
 *
 * Reads GET /api/admin/pii-access-log: every passport / PAN / Aadhaar
 * decrypt-reveal and bulk export, with who / when / what / how-many.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import Pagination from '@/components/list/Pagination';
import ExportButton from '@/components/list/ExportButton';
import { ShieldAlert, RefreshCw, History } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_META = {
  view_detail: { label: 'Viewed profile', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  view_documents: { label: 'Viewed documents', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  check_duplicate: { label: 'Duplicate check', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  document_expiry: { label: 'Doc-expiry report', cls: 'bg-teal-50 text-teal-700 border-teal-200' },
  tcs_report: { label: 'TCS report', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  gst_report: { label: 'GST report', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  gst_tcs_report: { label: 'GST/TCS report', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  bulk_export: { label: 'Bulk export', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const ACTION_OPTIONS = [
  ['all', 'All actions'],
  ['view_detail', 'Viewed profile'],
  ['view_documents', 'Viewed documents'],
  ['check_duplicate', 'Duplicate check'],
  ['document_expiry', 'Doc-expiry report'],
  ['tcs_report', 'TCS report'],
  ['gst_report', 'GST report'],
  ['gst_tcs_report', 'GST/TCS report'],
  ['bulk_export', 'Bulk export'],
];

const AUDIT_COLUMNS = [
  { key: 'timestamp', label: 'Timestamp (UTC ISO)' },
  { key: 'userEmail', label: 'User Email' },
  { key: 'userId', label: 'User Id' },
  { key: 'role', label: 'Role' },
  { key: 'action', label: 'Action' },
  { key: 'clientUcc', label: 'Subject UCC' },
  { key: 'fields', label: 'Fields', format: (r) => (Array.isArray(r.fields) ? r.fields.join('; ') : '') },
  { key: 'recordCount', label: 'Records' },
  { key: 'masked', label: 'Access', format: (r) => (r.masked ? 'Masked' : 'Full') },
  { key: 'subjectUccs', label: 'Subjects', format: (r) => (Array.isArray(r.subjectUccs) ? r.subjectUccs.join('; ') : '') },
  { key: 'path', label: 'Path' },
  { key: 'method', label: 'Method' },
];

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const LogRow = ({ e }) => {
  const meta = ACTION_META[e.action] || { label: e.action, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  const fields = Array.isArray(e.fields) ? e.fields : [];
  return (
    <tr className="hover:bg-slate-50 align-top" data-testid="audit-row">
      <td className="px-3 py-2 text-[11px] font-mono text-slate-600 whitespace-nowrap">{fmtWhen(e.timestamp)}</td>
      <td className="px-3 py-2">
        <div className="text-xs font-medium text-slate-800 truncate max-w-[200px]">{e.userEmail || e.userId || '—'}</div>
        <div className="text-[10px] text-slate-400 uppercase">{e.role || ''}</div>
      </td>
      <td className="px-3 py-2">
        <span className={`inline-flex items-center text-[10px] font-medium border rounded-full px-2 py-0.5 ${meta.cls}`}>{meta.label}</span>
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{e.clientUcc === '*' ? <span className="text-slate-400">bulk</span> : e.clientUcc}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {fields.map((f) => (
            <span key={f} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{f}</span>
          ))}
        </div>
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs text-slate-700">{e.recordCount}</td>
      <td className="px-3 py-2">
        {e.masked
          ? <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Masked</Badge>
          : <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">Full</Badge>}
      </td>
      <td className="px-3 py-2 font-mono text-[10px] text-slate-400 truncate max-w-[160px]">{e.path}</td>
    </tr>
  );
};

const ActivityLogs = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [action, setAction] = useState('all');
  const [userQ, setUserQ] = useState('');
  const [clientQ, setClientQ] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const buildParams = useCallback((limit, skip) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('skip', String(skip));
    if (action !== 'all') params.set('action', action);
    if (userQ.trim()) params.set('userId', userQ.trim());
    if (clientQ.trim()) params.set('clientUcc', clientQ.trim());
    if (start) params.set('start', start);
    if (end) params.set('end', `${end}T23:59:59.999999`);
    return params;
  }, [action, userQ, clientQ, start, end]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/pii-access-log?${buildParams(pageSize, (page - 1) * pageSize).toString()}`, { withCredentials: true });
      setEntries(res.data?.entries || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error('Failed to load activity log');
      setEntries([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, pageSize, buildParams]);

  const fetchAllForExport = useCallback(async () => {
    const all = [];
    const PAGE = 500;
    const CAP = 5000;
    let skip = 0;
    while (all.length < CAP) {
      const res = await axios.get(`${BACKEND_URL}/api/admin/pii-access-log?${buildParams(PAGE, skip).toString()}`, { withCredentials: true });
      const batch = res.data?.entries || [];
      all.push(...batch);
      const tot = res.data?.total || 0;
      if (batch.length < PAGE || all.length >= tot) break;
      skip += PAGE;
    }
    return all;
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => entries, [entries]);

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-6 text-center" data-testid="activity-logs-denied">
        <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-slate-900">Admin access required</h1>
        <p className="text-sm text-slate-500 mt-1">The PII access log is restricted to administrators.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-6" data-testid="activity-logs-page">
      <header className="mb-5 flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
          <History className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Activity Logs</h1>
          <p className="text-sm text-slate-500 mt-0.5">PII access audit — every passport / PAN / Aadhaar reveal &amp; export. {total} event{total === 1 ? '' : 's'}.</p>
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="w-[170px]">
          <Select value={action} onValueChange={(v) => { setPage(1); setAction(v); }}>
            <SelectTrigger className="h-9 text-xs" data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTION_OPTIONS.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Input className="h-9 w-[200px] text-xs" placeholder="User id / email" value={userQ}
          onChange={(e) => setUserQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setPage(1), load())}
          data-testid="audit-filter-user" />
        <Input className="h-9 w-[150px] text-xs" placeholder="Client UCC" value={clientQ}
          onChange={(e) => setClientQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setPage(1), load())}
          data-testid="audit-filter-client" />
        <Input type="date" className="h-9 w-[150px] text-xs" value={start}
          onChange={(e) => { setPage(1); setStart(e.target.value); }} data-testid="audit-filter-start" />
        <Input type="date" className="h-9 w-[150px] text-xs" value={end}
          onChange={(e) => { setPage(1); setEnd(e.target.value); }} data-testid="audit-filter-end" />
        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => { setPage(1); load(); }} data-testid="audit-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <div className="ml-auto">
          <ExportButton filename="pii-access-log" columns={AUDIT_COLUMNS} rows={entries}
            fetchRows={fetchAllForExport} testid="audit-export" />
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-[10px] uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Subject</th>
              <th className="px-3 py-2 font-medium">Fields</th>
              <th className="px-3 py-2 font-medium text-right">Records</th>
              <th className="px-3 py-2 font-medium">Access</th>
              <th className="px-3 py-2 font-medium">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-sm text-slate-400" data-testid="audit-empty">
                {loading ? 'Loading…' : 'No PII access events for this filter.'}
              </td></tr>
            ) : rows.map((e, i) => (<LogRow key={`${e.timestamp}-${i}`} e={e} />))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pageSize={pageSize} total={total}
        onPageChange={setPage} onPageSizeChange={(s) => { setPage(1); setPageSize(s); }}
        testid="audit-pagination" />
    </div>
  );
};

export default ActivityLogs;
